import {
  Box,
  Button,
  SimpleGrid,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
} from "@chakra-ui/react";
import { AvatarConfig, AVATAR_OPTIONS, DEFAULT_AVATAR_CONFIG } from "common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppPage } from "../utils/AppPage";
import { Avatar } from "./Avatar";
import { AvatarEditorPartSelection } from "./AvatarEditorPartSelection";
import { FaHatCowboy } from "react-icons/fa";
import { GiNoseFront } from "react-icons/gi";
import { GiLips } from "react-icons/gi";
import { FaRegEye } from "react-icons/fa";
import { GiBeard } from "react-icons/gi";
import { IoGlasses } from "react-icons/io5";
import { SiArchicad } from "react-icons/si";
import { FaTshirt } from "react-icons/fa";
import { FaShapes } from "react-icons/fa6";
import { FaHandPeace } from "react-icons/fa6";
import { IoIosColorPalette } from "react-icons/io";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { useAPI } from "../api/useAPI";
import { useAPIQuery } from "../api/useAPIQuery";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import { LOGGED_IN_HOME_PAGE } from "../config";
import { useNavigate } from "react-router";

export const AvatarEditorPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const api = useAPI();

  const { data, state, error, query } = useAPIQuery<"accountPut">({
    apiCall: api.accountPut,
  });
  useToastOnAPIError({ title: "Unable to update avatar", error });
  useOnAPISuccess<"accountPut">({
    data,
    state,
    onSuccess: () => {
      toast({
        position: "top",
        status: "success",
        title: "Successfully updated avatar",
      });
      navigate(LOGGED_IN_HOME_PAGE);
    },
  });
  const [beard, setBeard] = useState(DEFAULT_AVATAR_CONFIG.beard);
  const [body, setBody] = useState(DEFAULT_AVATAR_CONFIG.body);
  const [bodyIcon, setBodyIcon] = useState(DEFAULT_AVATAR_CONFIG.bodyIcon);
  const [brows, setBrows] = useState(DEFAULT_AVATAR_CONFIG.brows);
  const [eyes, setEyes] = useState(DEFAULT_AVATAR_CONFIG.eyes);
  const [gesture, setGesture] = useState(DEFAULT_AVATAR_CONFIG.gesture);
  const [glasses, setGlasses] = useState(DEFAULT_AVATAR_CONFIG.glasses);
  const [hair, setHair] = useState(DEFAULT_AVATAR_CONFIG.hair);
  const [lips, setLips] = useState(DEFAULT_AVATAR_CONFIG.lips);
  const [nose, setNose] = useState(DEFAULT_AVATAR_CONFIG.nose);
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_AVATAR_CONFIG.backgroundColor
  );

  /*
   * Load account
   */
  const {
    data: meData,
    state: meState,
    query: meQuery,
    error: meError,
  } = useAPIQuery<"me">({
    apiCall: api.me,
  });

  const isLoading = useMemo(() => meData === undefined, [meData]);

  useEffect(() => {
    if (meState !== "loading" && meData === undefined) {
      meQuery({});
    }
  }, [meData, meQuery, meState]);

  useToastOnAPIError({
    error: meError,
    title: `Unable to get account`,
  });

  useOnAPISuccess<"me">({
    data: meData,
    state: meState,
    onSuccess: (account) => {
      setBeard(account.avatarConfig.beard);
      setBody(account.avatarConfig.body);
      setBodyIcon(account.avatarConfig.bodyIcon);
      setBrows(account.avatarConfig.brows);
      setEyes(account.avatarConfig.eyes);
      setGesture(account.avatarConfig.gesture);
      setGlasses(account.avatarConfig.glasses);
      setHair(account.avatarConfig.hair);
      setLips(account.avatarConfig.lips);
      setNose(account.avatarConfig.nose);
      setBackgroundColor(account.avatarConfig.backgroundColor);
    },
  });

  const avatarConfig: AvatarConfig = useMemo(
    () => ({
      beard,
      body,
      bodyIcon,
      brows,
      eyes,
      gesture,
      glasses,
      hair,
      lips,
      nose,
      backgroundColor,
    }),
    [
      backgroundColor,
      beard,
      body,
      bodyIcon,
      brows,
      eyes,
      gesture,
      glasses,
      hair,
      lips,
      nose,
    ]
  );

  const onSave = useCallback(() => {
    const accountId = api.getAccountId();
    if (state !== "loading" && accountId) {
      query({
        id: accountId,
        avatarConfig,
      });
    }
  }, [api, avatarConfig, query, state]);

  return (
    <AppPage
      title="Edit Avatar"
      display={"flex"}
      flexDir={"column"}
      alignItems={"center"}
      customNavBar={
        <Box
          padding={4}
          width={"100%"}
          display={"flex"}
          justifyContent={"flex-end"}
        >
          <Button colorScheme={THEME_COLOR_SCHEME} onClick={onSave}>
            Save
          </Button>
        </Box>
      }
    >
      {!isLoading ? (
        <Avatar config={avatarConfig} style={{ height: "300px" }} />
      ) : (
        <Box display={"flex"} justifyContent={"center"} width={"100%"}>
          <Skeleton height={"300px"} width={"300px"} />
        </Box>
      )}
      <Tabs width={"100%"} variant={"soft-rounded"} paddingTop={2}>
        <TabList>
          <SimpleGrid
            minChildWidth={"50px"}
            spacing={2}
            width={"100%"}
            padding={2}
            border="1px solid gray"
            boxShadow={"2xl"}
            borderRadius={8}
          >
            <Tab>
              <FaHatCowboy />
            </Tab>
            <Tab>
              <GiNoseFront />
            </Tab>
            <Tab>
              <GiLips />
            </Tab>
            <Tab>
              <FaRegEye />
            </Tab>
            <Tab>
              <GiBeard />
            </Tab>
            <Tab>
              <IoGlasses />
            </Tab>
            <Tab>
              <SiArchicad />
            </Tab>
            <Tab>
              <FaTshirt />
            </Tab>
            <Tab>
              <FaShapes />
            </Tab>
            <Tab>
              <FaHandPeace />
            </Tab>
            <Tab>
              <IoIosColorPalette />
            </Tab>
          </SimpleGrid>
        </TabList>
        <TabPanels>
          <TabPanel>
            <AvatarEditorPartSelection
              part={hair}
              setPart={setHair}
              options={AVATAR_OPTIONS.hair}
              toAvatarConfig={(part) => ({ ...avatarConfig, hair: part })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={nose}
              setPart={setNose}
              options={AVATAR_OPTIONS.nose}
              toAvatarConfig={(nose) => ({ ...avatarConfig, nose })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={lips}
              setPart={setLips}
              options={AVATAR_OPTIONS.lips}
              toAvatarConfig={(lips) => ({ ...avatarConfig, lips })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={eyes}
              setPart={setEyes}
              options={AVATAR_OPTIONS.eyes}
              toAvatarConfig={(eyes) => ({ ...avatarConfig, eyes })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={beard}
              setPart={setBeard}
              options={AVATAR_OPTIONS.beard}
              toAvatarConfig={(beard) => ({ ...avatarConfig, beard })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={glasses}
              setPart={setGlasses}
              options={AVATAR_OPTIONS.glasses}
              toAvatarConfig={(glasses) => ({ ...avatarConfig, glasses })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={brows}
              setPart={setBrows}
              options={AVATAR_OPTIONS.brows}
              toAvatarConfig={(brows) => ({ ...avatarConfig, brows })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={body}
              setPart={setBody}
              options={AVATAR_OPTIONS.body}
              toAvatarConfig={(body) => ({ ...avatarConfig, body })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={bodyIcon}
              setPart={setBodyIcon}
              options={AVATAR_OPTIONS.bodyIcon}
              toAvatarConfig={(bodyIcon) => ({ ...avatarConfig, bodyIcon })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={gesture}
              setPart={setGesture}
              options={AVATAR_OPTIONS.gesture}
              toAvatarConfig={(gesture) => ({ ...avatarConfig, gesture })}
              isLoading={isLoading}
            />
          </TabPanel>
          <TabPanel>
            <AvatarEditorPartSelection
              part={backgroundColor}
              setPart={setBackgroundColor}
              options={AVATAR_OPTIONS.backgroundColor}
              toAvatarConfig={(backgroundColor) => ({
                ...avatarConfig,
                backgroundColor,
              })}
              isLoading={isLoading}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </AppPage>
  );
};
